import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { applyTheme } from "@/lib/themes";

const AuthContext = createContext(null);

/**
 * Session 43: the JWT is held in an HttpOnly cookie, unreachable from
 * JS. We bootstrap the user by calling /auth/me with cookies attached
 * (axios is configured with withCredentials). If that 200s we're
 * signed in, otherwise we're not.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await api.get("/users/me/permissions");
      setPermissions(res.data.permissions);
    } catch {
      setPermissions(null);
    }
  }, []);

  const loadUser = useCallback(async () => {
    // /auth/me returns 401 when no cookie is present → signed out.
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      // Persist a minimal copy to localStorage so non-React utilities
      // (e.g. lib/timezone.userTz()) can resolve the viewer's profile
      // timezone without threading the user object through props. The
      // HttpOnly auth cookie remains the source of truth for auth —
      // this is purely a presentation hint.
      try { localStorage.setItem("eyvo_user", JSON.stringify(res.data)); } catch {}
      applyTheme(res.data.color_theme || "default");
      try {
        const permRes = await api.get("/users/me/permissions");
        setPermissions(permRes.data.permissions);
      } catch { setPermissions(null); }
    } catch {
      // 401 → unauthenticated. Nothing to clean up on the client.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const loginWithGoogle = async (credential) => {
    // Backend sets the HttpOnly cookie on this response; no token
    // storage needed client-side.
    const res = await api.post("/auth/google", { credential });
    setUser(res.data.user);
    try { localStorage.setItem("eyvo_user", JSON.stringify(res.data.user)); } catch {}
    applyTheme(res.data.user.color_theme || "default");
    loadPermissions();
    return res.data;
  };

  const loginWithEmail = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    // For the client (TOTP) flow the response is a multi-step chain
    // that finishes on verify-totp. For internal users it returns a
    // full session here. Either way the cookie is set by the backend.
    if (res.data.status === "authenticated") {
      setUser(res.data.user);
      try { localStorage.setItem("eyvo_user", JSON.stringify(res.data.user)); } catch {}
      applyTheme(res.data.user.color_theme || "default");
      loadPermissions();
    }
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore — cookie already gone client-side */ }
    setUser(null);
    setPermissions(null);
    try { localStorage.removeItem("eyvo_user"); } catch {}
  };

  /** Check if user can access a module: "write", "read", or "hidden" */
  const hasModuleAccess = useCallback((module) => {
    if (!permissions) return "hidden";
    return permissions[module] || "hidden";
  }, [permissions]);

  /** Quick boolean check: can the user see this module at all? */
  const canSee = useCallback((module) => {
    const level = hasModuleAccess(module);
    return level === "write" || level === "read";
  }, [hasModuleAccess]);

  /** Can the user write (create/edit/delete) in this module? */
  const canWrite = useCallback((module) => {
    return hasModuleAccess(module) === "write";
  }, [hasModuleAccess]);

  /** True if the user has the given role — checks both the legacy
   *  `user.role` column and the new multi-role `user.roles[]` array
   *  (baked into the /auth/me response). */
  const hasRole = useCallback((roleName) => {
    if (!user) return false;
    if (user.role === roleName) return true;
    return Array.isArray(user.roles) && user.roles.includes(roleName);
  }, [user]);

  const isPlatformAdmin = useCallback(() => hasRole("platform_admin"), [hasRole]);

  return (
    <AuthContext.Provider value={{ user, setUser, permissions, loading, loginWithGoogle, loginWithEmail, logout, loadUser, loadPermissions, hasModuleAccess, canSee, canWrite, hasRole, isPlatformAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
